import React, { useState } from 'react';
import { FiRotateCcw } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PAYMENT_REFUND } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './Payments.css';
import './Refund.css';

function Refund() {
  const [userId, setUserId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const userIdTrim = (userId || '').trim();
  const phoneTrim = (phoneNumber || '').trim();
  const hasIdentifier = userIdTrim || phoneTrim;
  const reasonTrim = (refundReason || '').trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!hasIdentifier) {
      setError('Enter at least one: User ID or Phone number.');
      return;
    }
    if (!reasonTrim) {
      setError('Refund reason is required.');
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmYes = async () => {
    setError('');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        refund_reason: reasonTrim,
        ...(userIdTrim ? { user_id: userIdTrim } : {}),
        ...(phoneTrim ? { phone_number: phoneTrim } : {}),
      };
      const res = await fetch(apiUrl(PAYMENT_REFUND.path), {
        method: PAYMENT_REFUND.method,
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setShowConfirm(false);
        setSuccess(true);
        setUserId('');
        setPhoneNumber('');
        setRefundReason('');
        setTimeout(() => setSuccess(false), 4000);
      } else {
        setError(getApiErrorMessage(data, res.status, { default: 'Refund could not be processed. Please try again.' }));
      }
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancel = () => {
    if (!submitting) setShowConfirm(false);
  };

  return (
    <div className="page payments-page refund-page">
      <div className="payments-header">
        <h3>Refund</h3>
        <p className="payments-subtitle">
          Enter User ID or Phone number (at least one required) and a refund reason to process a refund.
        </p>
      </div>

      {success && (
        <div className="refund-success" role="status">
          Refund request submitted successfully.
        </div>
      )}

      <form onSubmit={handleSubmit} className="payments-search-card refund-form">
        <div className="refund-fields">
          <div className="refund-row">
            <label htmlFor="refund-user-id" className="payments-search-label">
              User ID
            </label>
            <input
              id="refund-user-id"
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="e.g. UUID"
              className="payments-search-input refund-input"
              autoComplete="off"
            />
          </div>
          <div className="refund-row">
            <label htmlFor="refund-phone" className="payments-search-label">
              Phone number
            </label>
            <input
              id="refund-phone"
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. +91-9876543210"
              className="payments-search-input refund-input"
              autoComplete="off"
            />
          </div>
          <p className="refund-hint">At least one of User ID or Phone number is required <span className="label-required" aria-hidden="true">*</span>.</p>
          <div className="refund-row">
            <label htmlFor="refund-reason" className="payments-search-label">
              Refund reason <span className="refund-required">*</span>
            </label>
            <textarea
              id="refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Enter reason for refund..."
              rows={4}
              className="refund-textarea"
              autoComplete="off"
            />
          </div>
        </div>
        {error && <p className="refund-error" role="alert">{error}</p>}
        <div className="refund-actions">
          <button type="submit" className="refund-btn" disabled={submitting}>
            <FiRotateCcw className="refund-btn-icon" aria-hidden />
            Refund
          </button>
        </div>
      </form>

      {showConfirm && (
        <div
          className="refund-confirm-overlay"
          onClick={handleConfirmCancel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="refund-confirm-title"
        >
          <div className="refund-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h4 id="refund-confirm-title" className="refund-confirm-title">
              Confirm refund
            </h4>
            <p className="refund-confirm-desc">Do you want to proceed with this refund?</p>
            <div className="refund-confirm-actions">
              <button
                type="button"
                className="refund-confirm-btn refund-confirm-no"
                onClick={handleConfirmCancel}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="refund-confirm-btn refund-confirm-yes"
                onClick={handleConfirmYes}
                disabled={submitting}
              >
                {submitting ? 'Processing…' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Refund;
